// Layer 4 - 4 years - Counting, letters, memory, spatial branching (12 games total)
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { playSfx, speak } from "@/lib/sounds";
import { t } from "@/lib/translations";

const playSfxHelper = (name, soundOn) => {
  try {
    playSfx(name, soundOn);
  } catch (e) {
    console.warn("Sound play failed", e);
  }
};

// ==========================================
// 1) Count Apples
// ==========================================
function CountApples({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [round, setRound] = useState(0);
  const apples = useMemo(() => 3 + Math.floor(Math.random() * 6), [round]);
  const [score, setScore] = useState(0);
  const choices = useMemo(() => {
    const set = new Set([apples]);
    while (set.size < 4) {
      set.add(Math.max(1, apples + Math.floor(Math.random() * 5 - 2)));
    }
    return Array.from(set).sort(() => Math.random() - 0.5);
  }, [apples]);

  const pick = (n) => {
    if (n === apples) {
      playSfxHelper("success", soundOn);
      const s = score + 1;
      setScore(s);
      if (s >= 3) {
        setTimeout(() => onWin(), 1200);
        setScore(0);
      } else {
        setRound((r) => r + 1);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-count">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-3 text-center">
        {t("count_total", language)}
      </div>
      <div className="bg-white rounded-3xl p-5 border-4 border-red-200 shadow-[0_6px_0_#fca5a5] flex flex-wrap gap-2 justify-center max-w-lg min-h-[100px]">
        {Array.from({ length: apples }).map((_, i) => (
          <motion.span
            key={`apple-${round}-${i}`}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: i * 0.05 }}
            className="text-4xl sm:text-5xl select-none"
          >
            🍎
          </motion.span>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-3 mt-6">
        {choices.map((c) => (
          <motion.button
            key={c}
            whileTap={{ scale: 0.9 }}
            onClick={() => pick(c)}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border-4 border-red-300 font-display font-bold text-3xl text-slate-700 shadow-md"
            style={{ boxShadow: "0 5px 0 #f87171" }}
            data-testid={`count-choice-${c}`}
          >
            {c}
          </motion.button>
        ))}
      </div>
      <div className="mt-4 font-bold text-slate-500">⭐ {score}/3</div>
    </div>
  );
}

// ==========================================
// 2) Find Letters
// ==========================================
function FindLetters({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const letters = "ABCDEFG".split("");
  const [target, setTarget] = useState(() => letters[Math.floor(Math.random() * letters.length)]);
  const [score, setScore] = useState(0);

  const pick = (l) => {
    if (l === target) {
      playSfxHelper("success", soundOn);
      const s = score + 1;
      setScore(s);
      if (s >= 4) {
        setTimeout(() => onWin(), 1200);
        setScore(0);
      } else {
        let next;
        do {
          next = letters[Math.floor(Math.random() * letters.length)];
        } while (next === target);
        setTarget(next);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-letters">
      <div className="font-display text-2xl sm:text-3xl font-bold text-slate-700 mb-3 text-center">
        {t("find_letter", language)}
        <span className="font-display text-4xl ml-2 font-black text-red-500">
          {target}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-3 max-w-md">
        {letters.map((l) => (
          <motion.button
            key={l}
            whileTap={{ scale: 0.88 }}
            onClick={() => pick(l)}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border-4 border-red-300 font-display font-bold text-3xl text-slate-700"
            style={{ boxShadow: "0 5px 0 #f87171" }}
            data-testid={`letter-${l}`}
          >
            {l}
          </motion.button>
        ))}
      </div>
      <div className="mt-4 font-bold text-slate-500">⭐ {score}/4</div>
    </div>
  );
}

// ==========================================
// 3) Memory Match
// ==========================================
function MemoryMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const symbols = ["🐻", "🌸", "⭐", "🍎"];
  const [cards, setCards] = useState(() =>
    [...symbols, ...symbols]
      .sort(() => Math.random() - 0.5)
      .map((s, i) => ({ id: i, symbol: s, flipped: false, matched: false }))
  );
  const [picked, setPicked] = useState([]);

  const flip = (idx) => {
    if (cards[idx].flipped || cards[idx].matched || picked.length === 2) return;
    playSfxHelper("tap", soundOn);
    const newCards = cards.map((c, i) => (i === idx ? { ...c, flipped: true } : c));
    setCards(newCards);
    const newPicked = [...picked, idx];
    setPicked(newPicked);
    if (newPicked.length === 2) {
      const [a, b] = newPicked;
      if (newCards[a].symbol === newCards[b].symbol) {
        setTimeout(() => {
          playSfxHelper("success", soundOn);
          setCards((cs) =>
            cs.map((c, i) => (i === a || i === b ? { ...c, matched: true } : c))
          );
          setPicked([]);
        }, 400);
      } else {
        setTimeout(() => {
          setCards((cs) =>
            cs.map((c, i) => (i === a || i === b ? { ...c, flipped: false } : c))
          );
          setPicked([]);
        }, 800);
      }
    }
  };

  useEffect(() => {
    if (cards.length > 0 && cards.every((c) => c.matched)) {
      setTimeout(() => onWin(), 1200);
    }
  }, [cards, onWin]);

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-memory">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-3 text-center">
        {t("match_pairs", language)}
      </div>
      <div className="grid grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c, i) => (
          <motion.button
            key={c.id}
            whileTap={{ scale: 0.9 }}
            onClick={() => flip(i)}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center text-4xl sm:text-5xl border-4 border-white shadow-md"
            style={{
              background: c.flipped || c.matched ? "white" : "#F87171",
              boxShadow: `0 5px 0 #DC2626`,
              opacity: c.matched ? 0.4 : 1,
            }}
            data-testid={`memory-card-${i}`}
          >
            {c.flipped || c.matched ? c.symbol : "?"}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// 4) Frog Hop Counting (Game 4)
// ==========================================
function FrogHopCounting({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [activeNum, setActiveNum] = useState(1);

  const handleHop = (num) => {
    if (num === activeNum) {
      playSfxHelper("success", soundOn);
      const next = activeNum + 1;
      setActiveNum(next);
      if (next > 5) {
        setTimeout(() => onWin(), 1200);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Bantu Katak Melompat Berurutan (1-5)! 🐸" : "Help the Frog Hop in Order (1-5)! 🐸"}
      </div>

      <div className="relative w-full max-w-md h-40 bg-emerald-50 rounded-3xl border-4 border-emerald-200 p-4 flex justify-between items-center shadow-inner overflow-hidden">
        {/* Floating frog indicator */}
        <div className="absolute top-2 w-full flex justify-center text-5xl">
          🐸
        </div>

        {[1, 2, 3, 4, 5].map((n) => {
          const isDone = n < activeNum;
          const isActive = n === activeNum;
          return (
            <motion.button
              key={n}
              onClick={() => handleHop(n)}
              whileTap={{ scale: 0.9 }}
              className={`w-14 h-14 rounded-full flex items-center justify-center font-display font-extrabold text-xl shadow-lg border-2 ${
                isDone
                  ? "bg-emerald-500 text-white border-emerald-600"
                  : isActive
                  ? "bg-amber-400 text-slate-800 border-amber-500 animate-pulse"
                  : "bg-white text-slate-400 border-slate-200"
              }`}
            >
              {n}
            </motion.button>
          );
        })}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Urutan Angka & One-to-One Correspondence" : "Trains Stable-Order & One-to-One Counting"}
      </div>
    </div>
  );
}

// ==========================================
// 5) Rainbow Xylophone (Game 5)
// ==========================================
function RainbowXylophone({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [sequence, setSequence] = useState([]);
  const keys = [
    { id: 1, label: "Do", color: "bg-red-400" },
    { id: 2, label: "Re", color: "bg-yellow-400" },
    { id: 3, label: "Mi", color: "bg-green-400" },
    { id: 4, label: "Fa", color: "bg-sky-400" },
    { id: 5, label: "Sol", color: "bg-purple-400" },
  ];

  const handleKeyClick = (id) => {
    playSfxHelper("pop", soundOn);
    setSequence((prev) => {
      const next = [...prev, id];
      if (next.length >= 4) {
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Melodi Xilofon Pelangi! 🌈" : "Play the Rainbow Xylophone! 🌈"} ({sequence.length}/4)
      </div>

      <div className="flex gap-2 p-6 rounded-3xl bg-amber-50 border-4 border-yellow-200 shadow-xl max-w-sm w-full">
        {keys.map((k) => (
          <motion.button
            key={k.id}
            onClick={() => handleKeyClick(k.id)}
            whileTap={{ scale: 0.95, y: 5 }}
            className={`flex-1 ${k.color} h-48 rounded-2xl border-b-8 border-black/20 shadow-md flex items-end justify-center pb-4`}
          >
            <span className="text-white font-display font-extrabold text-sm uppercase tracking-wider select-none">
              {k.label}
            </span>
          </motion.button>
        ))}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Meningkatkan Integrasi Visual-Auditorik & Motorik Halus" : "Enhances Visual-Auditory Integration & Fine Motor skills"}
      </div>
    </div>
  );
}

// ==========================================
// 6) Crazy Wheel Match (Game 6)
// ==========================================
function CrazyWheelMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleChoice = (emoji) => {
    if (emoji === "🍓") {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Kartu Buah yang Berputar! 🎡" : "Tap the Spinning Fruit Card! 🎡"}
      </div>

      {/* Spinning wheel visualization */}
      <div className="relative w-44 h-44 rounded-full border-8 border-red-300 bg-white flex items-center justify-center shadow-xl">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
          className="text-6xl select-none"
        >
          🍓
        </motion.div>
      </div>

      <div className="flex gap-4">
        {["🍌", "🍓", "🍎"].map((emoji) => (
          <motion.button
            key={emoji}
            whileTap={{ scale: 0.85 }}
            onClick={() => handleChoice(emoji)}
            disabled={success}
            className="w-20 h-20 rounded-2xl bg-white border-4 border-red-200 shadow-lg flex items-center justify-center text-4xl select-none"
          >
            {emoji}
          </motion.button>
        ))}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Atensi Fleksibel & Kontrol Motorik Adaptif" : "Trains Attention Flexibility & Adaptive Motor Control"}
      </div>
    </div>
  );
}

// ==========================================
// 7) Ant Cookie Maze (Game 7)
// ==========================================
function AntCookieMaze({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handlePath = (isRight) => {
    if (isRight) {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Pilih Jalur Aman untuk Semut! 🐜" : "Choose the Safe Path for the Ant! 🐜"}
      </div>

      <div className="w-80 h-48 bg-orange-50/50 border-4 border-red-200 rounded-3xl p-6 flex flex-col justify-between shadow-xl relative">
        <div className="flex justify-between items-center">
          <motion.span
            animate={success ? { x: 200 } : {}}
            transition={{ duration: 1 }}
            className="text-5xl select-none"
          >
            🐜
          </motion.span>
          <span className="text-5xl select-none">🍪</span>
        </div>

        <div className="flex justify-around mt-4">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => handlePath(false)}
            disabled={success}
            className="px-4 py-2 rounded-xl bg-red-400 text-white font-display font-black text-sm shadow shadow-red-200"
          >
            {language === "id" ? "Jalur Atas 🚧" : "Upper Path 🚧"}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => handlePath(true)}
            disabled={success}
            className="px-4 py-2 rounded-xl bg-emerald-500 text-white font-display font-black text-sm shadow shadow-emerald-200"
          >
            {language === "id" ? "Jalur Bawah ✅" : "Lower Path ✅"}
          </motion.button>
        </div>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Pemecahan Masalah Spasial Bercabang" : "Trains Branched Spatial Problem Solving"}
      </div>
    </div>
  );
}

// ==========================================
// 8) Cloud Shadow Shape (Game 8)
// ==========================================
function CloudShadowShape({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleMatch = (shape) => {
    if (shape === "heart") {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Cari Balon yang Sama dengan Awan! ☁️" : "Find the Balloon Matching the Cloud! ☁️"}
      </div>

      <div className="w-56 h-36 bg-sky-100 rounded-3xl border-4 border-sky-300 flex items-center justify-center shadow-inner relative">
        <span className="text-8xl select-none opacity-40 absolute">☁️</span>
        <span className="text-5xl select-none z-10">❤️</span>
      </div>

      <div className="flex gap-4">
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={() => handleMatch("star")}
          disabled={success}
          className="w-20 h-20 rounded-2xl bg-white border-4 border-red-200 shadow-lg flex items-center justify-center text-4xl select-none"
        >
          ⭐
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={() => handleMatch("heart")}
          disabled={success}
          className="w-20 h-20 rounded-2xl bg-white border-4 border-red-200 shadow-lg flex items-center justify-center text-4xl select-none"
        >
          ❤️
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Fleksibilitas Representasi Visual Objek" : "Trains Representational Flexibility of Shapes"}
      </div>
    </div>
  );
}

// ==========================================
// 9) Pizza Topping Match (Game 9)
// ==========================================
function PizzaToppingMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [toppings, setToppings] = useState([]);

  const handleToppingClick = (id) => {
    playSfxHelper("pop", soundOn);
    setToppings((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      if (next.length >= 3) {
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Tambahkan 3 Topping Tomat ke Pizza! 🍕" : "Add 3 Tomato Toppings to Pizza! 🍕"} ({toppings.length}/3)
      </div>

      <div className="relative w-56 h-56 rounded-full bg-amber-400 border-8 border-amber-600 shadow-2xl flex items-center justify-center overflow-hidden">
        <span className="text-[10rem] select-none opacity-90">🍕</span>
        {/* Render successfully placed toppings */}
        {toppings.map((t) => (
          <motion.span
            key={t}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute text-4xl select-none"
            style={{
              left: t === 1 ? "30%" : t === 2 ? "55%" : "40%",
              top: t === 1 ? "35%" : t === 2 ? "45%" : "60%",
            }}
          >
            🍅
          </motion.span>
        ))}
      </div>

      <div className="flex gap-4">
        {[1, 2, 3].map((t) => {
          const isPlaced = toppings.includes(t);
          return (
            <motion.button
              key={t}
              onClick={() => handleToppingClick(t)}
              disabled={isPlaced}
              whileTap={{ scale: 0.85 }}
              className="w-16 h-16 rounded-2xl bg-white border-4 border-red-200 shadow-lg flex items-center justify-center text-3xl select-none"
              style={{ opacity: isPlaced ? 0.3 : 1 }}
            >
              🍅
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// 10) Whack-a-Squirrel (Game 10)
// ==========================================
function WhackASquirrel({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [activeHole, setActiveHole] = useState(0);
  const [hits, setHits] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveHole(Math.floor(Math.random() * 3));
    }, 1500);
    return () => clearInterval(timer);
  }, []);

  const handleHit = (idx) => {
    if (idx === activeHole) {
      playSfxHelper("star", soundOn);
      const next = hits + 1;
      setHits(next);
      if (next >= 4) {
        setTimeout(() => onWin(), 1000);
      } else {
        setActiveHole((activeHole + 1) % 3);
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Tupai yang Muncul dari Lubang! 🐿️" : "Tap the Squirrel Popping out! 🐿️"} ({hits}/4)
      </div>

      <div className="flex gap-6 p-6 rounded-3xl bg-emerald-50 border-4 border-emerald-200 shadow-xl w-full max-w-sm justify-around">
        {[0, 1, 2].map((idx) => {
          const isActive = idx === activeHole;
          return (
            <div key={idx} className="flex flex-col items-center gap-2">
              <motion.button
                onClick={() => handleHit(idx)}
                whileTap={{ scale: 0.8 }}
                className={`w-20 h-20 rounded-full border-4 shadow-md flex items-center justify-center text-4xl select-none transition-colors ${
                  isActive ? "bg-amber-100 border-amber-400" : "bg-slate-700 border-slate-800"
                }`}
              >
                {isActive ? "🐿️" : "🕳️"}
              </motion.button>
            </div>
          );
        })}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Refleks & Kewaspadaan Visual Motorik" : "Trains Visual Motor Alertness & Reaction Time"}
      </div>
    </div>
  );
}

// ==========================================
// 11) Farmyard Sound Match (Game 11)
// ==========================================
function FarmyardSoundMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleMatch = (animal) => {
    if (animal === "cow") {
      playSfxHelper("success", soundOn);
      setSuccess(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  const playAnimalVoice = () => {
    playSfxHelper("moo", soundOn);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Dengarkan Suara dan Temukan Hewannya! 🚜" : "Listen and Find the Farm Animal! 🚜"}
      </div>

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={playAnimalVoice}
        className="px-6 py-4 rounded-3xl bg-amber-400 text-slate-800 border-4 border-amber-500 font-display font-black text-lg shadow-xl"
      >
        🔊 {language === "id" ? "Putar Suara (Moo!)" : "Play Sound (Moo!)"}
      </motion.button>

      <div className="flex gap-4 mt-4">
        {[
          { id: "duck", emoji: "🦆" },
          { id: "cow", emoji: "🐮" },
          { id: "sheep", emoji: "🐑" },
        ].map((an) => (
          <motion.button
            key={an.id}
            whileTap={{ scale: 0.85 }}
            onClick={() => handleMatch(an.id)}
            disabled={success}
            className="w-20 h-20 rounded-2xl bg-white border-4 border-red-200 shadow-lg flex items-center justify-center text-5xl select-none"
          >
            {an.emoji}
          </motion.button>
        ))}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Memperkuat Memori Asosiatif Lintas Sensorik (Cross-Modal)" : "Reinforces Visual-Auditory Cross-Modal Associations"}
      </div>
    </div>
  );
}

// ==========================================
// 12) Window Sweeper (Game 12)
// ==========================================
function WindowSweeper({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [revealed, setRevealed] = useState(false);
  const [drops, setDrops] = useState(() =>
    Array.from({ length: 9 }, (_, i) => ({
      id: i,
      wiped: false,
    }))
  );

  const sweepDrop = (id) => {
    if (revealed) return;
    playSfxHelper("pop", soundOn);
    setDrops((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, wiped: true } : p));
      const wipedCount = next.filter((p) => p.wiped).length;
      if (wipedCount === prev.length) {
        setRevealed(true);
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Usap Jendela Kaca yang Berembun! 🪟" : "Sweep the Foggy Window! 🪟"}
      </div>

      <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-3xl overflow-hidden border-4 border-red-200 shadow-2xl bg-sky-200 flex items-center justify-center">
        <motion.div
          animate={revealed ? { scale: [1, 1.25, 1], rotate: [0, 15, -15, 0] } : {}}
          transition={{ duration: 1 }}
          className="text-8xl select-none"
        >
          🌅
        </motion.div>

        {!revealed && (
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 bg-blue-300/40">
            {drops.map((p) => (
              <motion.button
                key={p.id}
                onMouseEnter={() => sweepDrop(p.id)}
                onTouchStart={() => sweepDrop(p.id)}
                onClick={() => sweepDrop(p.id)}
                animate={{ opacity: p.wiped ? 0 : 1 }}
                className="w-full h-full bg-slate-100 border border-slate-200/40 flex items-center justify-center text-4xl select-none opacity-90"
              >
                🌫️
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Integrasi Sensori Motorik Halus Jari" : "Stimulates Fine Finger Visual-Sensory Integration"}
      </div>
    </div>
  );
}

// Export Layer 4 games list
export const Layer4Games = {
  count: { icon: "🔢", Component: CountApples },
  letters: { icon: "🔤", Component: FindLetters },
  memory: { icon: "🃏", Component: MemoryMatch },
  frog_hop: { icon: "🐸", Component: FrogHopCounting },
  xylophone: { icon: "🎼", Component: RainbowXylophone },
  wheel_match: { icon: "🎡", Component: CrazyWheelMatch },
  ant_maze: { icon: "🐜", Component: AntCookieMaze },
  cloud_shape: { icon: "☁️", Component: CloudShadowShape },
  pizza_toppings: { icon: "🍕", Component: PizzaToppingMatch },
  squirrel_tap: { icon: "🐿️", Component: WhackASquirrel },
  farm_match: { icon: "🚜", Component: FarmyardSoundMatch },
  window_sweeper: { icon: "🪟", Component: WindowSweeper },
};
